function getPostFiltersGroup(element) {
  return element?.closest('.gb-component-post-filters') || null;
}

function getPostFiltersGroupForElement(element) {
  const directGroup = getPostFiltersGroup(element);

  if (directGroup) {
    return directGroup;
  }

  const section = element?.closest('.gb-section, section');

  return section?.querySelector('.gb-component-post-filters') || null;
}

function normalizeQueryKey(queryKey) {
  if (!queryKey) {
    return null;
  }

  const normalizedQueryKey = String(queryKey).split('?')[0].trim();

  if (!/^query-[A-Za-z0-9_-]+$/.test(normalizedQueryKey)) {
    return null;
  }

  return normalizedQueryKey;
}

function getQueryKeyFromGroup(postFiltersGroup) {
  if (!postFiltersGroup) {
    return null;
  }

  const explicitQueryKey = postFiltersGroup.querySelector('[data-query-key]')
    ?.dataset.queryKey;

  if (explicitQueryKey) {
    return normalizeQueryKey(explicitQueryKey);
  }

  const section = postFiltersGroup.closest('.gb-section, section') || document;
  const routerRegions = [...section.querySelectorAll('[data-gb-router-region]')];

  if (!routerRegions.length) {
    return null;
  }

  const closestRegion = routerRegions.reduce((bestMatch, routerRegion) => {
    const distance = Math.abs(
      routerRegion.getBoundingClientRect().top -
        postFiltersGroup.getBoundingClientRect().top
    );

    if (!bestMatch || distance < bestMatch.distance) {
      return {
        distance,
        key: routerRegion.getAttribute('data-gb-router-region'),
      };
    }

    return bestMatch;
  }, null);

  return normalizeQueryKey(closestRegion?.key || routerRegions[0].getAttribute('data-gb-router-region'));
}

function getReplaceableTargets(context = document, queryKey = null) {
  if (!queryKey) {
    return [
      ...context.querySelectorAll(
        '[data-gb-router-region], .gb-looper, .gb-query-loop-pagination'
      ),
    ];
  }

  const routerRegion = context.querySelector(
    `[data-gb-router-region="${queryKey}"]`
  );

  if (!routerRegion) {
    return [];
  }

  return [routerRegion];
}

function getPostSearches(context = document) {
  return [...context.querySelectorAll('[data-post-search]')];
}

function getPostSorts(context = document) {
  return [...context.querySelectorAll('[data-post-sort]')];
}

function getPostCalendars(context = document) {
  return [...context.querySelectorAll('[data-post-calendar]')];
}

function getInteractivePostDropdowns(context = document) {
  return [
    ...context.querySelectorAll('[data-post-filter], [data-post-sort]'),
  ];
}

function getPostFiltersState(postFiltersGroup) {
  return {
    postFiltersGroup,
    postFilters: [...postFiltersGroup.querySelectorAll('[data-post-filter]')],
    postSearches: getPostSearches(postFiltersGroup),
    postSorts: getPostSorts(postFiltersGroup),
    postCalendars: getPostCalendars(postFiltersGroup),
    interactiveDropdowns: getInteractivePostDropdowns(postFiltersGroup),
    accordionButtons: postFiltersGroup.querySelectorAll(
      '.gb-component-post-filters__accordion'
    ),
    queryKey: getQueryKeyFromGroup(postFiltersGroup),
  };
}

function resetGbPaginationParams(searchParams, queryKey = null) {
  if (queryKey) {
    searchParams.delete(`${queryKey}-page`);
    return;
  }

  [...searchParams.keys()].forEach((param) => {
    if (param.startsWith('query-') && param.endsWith('-page')) {
      searchParams.delete(param);
    }
  });
}

function getScopedFilterParam(param, queryKey = null) {
  const normalizedQueryKey = normalizeQueryKey(queryKey);

  return normalizedQueryKey ? `${normalizedQueryKey}-${param}` : param;
}

function getPostFilterValues(postFilter) {
  return [
    ...postFilter.querySelectorAll(
      '.gb-component-post-filters__checkbox-input:checked'
    ),
  ].map((checkbox) => checkbox.value);
}

function updatePostFilterButtonLabel(postFilter) {
  const buttonLabel = postFilter.querySelector('[data-post-filter-label]');
  const baseLabel = postFilter.dataset.label;
  const activeCount = getPostFilterValues(postFilter).length;

  if (!buttonLabel || !baseLabel) return;

  buttonLabel.textContent =
    activeCount > 0 ? `${baseLabel} (${activeCount})` : baseLabel;
}

function syncPostSearchesFromUrl(postSearches, url, queryKey = null) {
  postSearches.forEach((postSearch) => {
    const postSearchInput = postSearch.querySelector(
      '.gb-component-post-filters__search-input'
    );

    if (!postSearchInput) return;

    postSearchInput.value =
      url.searchParams.get(getScopedFilterParam('filter_search', queryKey)) ||
      url.searchParams.get('filter_search') ||
      '';
  });
}

function syncPostFilterInputsFromUrl(postFilters, url, queryKey = null) {
  postFilters.forEach((postFilter) => {
    const filterParam = postFilter.dataset.param;
    const activeValues = new Set(
      (
        url.searchParams.get(getScopedFilterParam(filterParam, queryKey)) ||
        url.searchParams.get(filterParam) ||
        ''
      )
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    );

    postFilter
      .querySelectorAll('.gb-component-post-filters__checkbox-input')
      .forEach((checkbox) => {
        checkbox.checked = activeValues.has(checkbox.value);
      });

    updatePostFilterButtonLabel(postFilter);
  });
}

function closePostFilterMenus(postFilters, exceptPostFilter = null) {
  postFilters.forEach((postFilter) => {
    if (exceptPostFilter && postFilter === exceptPostFilter) return;

    const button = postFilter.querySelector(
      '.gb-component-post-filters__dropdown'
    );
    const menu = postFilter.querySelector('.gb-component-post-filters__menu');

    if (!button || !menu) return;

    button.setAttribute('aria-expanded', 'false');
    postFilter.classList.remove('gb-component-post-filters__filter--open');
    menu.hidden = true;
  });
}

function buildPostFilterUrl(
  postFilters,
  postSearches,
  baseUrl,
  { resetPagination = false, queryKey = null } = {}
) {
  const url = new URL(baseUrl, window.location.origin);

  postFilters.forEach((postFilter) => {
    const filterParam = postFilter.dataset.param;

    if (!filterParam) return;

    const selectedValues = getPostFilterValues(postFilter);
    const scopedParam = getScopedFilterParam(filterParam, queryKey);

    if (selectedValues.length) {
      url.searchParams.set(scopedParam, selectedValues.join(','));
    } else {
      url.searchParams.delete(scopedParam);
    }
  });

  postSearches.forEach((postSearch) => {
    const postSearchInput = postSearch.querySelector(
      '.gb-component-post-filters__search-input'
    );

    if (!postSearchInput) return;

    if (postSearchInput.value.trim()) {
      url.searchParams.set(
        getScopedFilterParam('filter_search', queryKey),
        postSearchInput.value.trim()
      );
    } else {
      url.searchParams.delete(getScopedFilterParam('filter_search', queryKey));
    }
  });

  if (resetPagination) {
    resetGbPaginationParams(url.searchParams, queryKey);
  }

  return url;
}

function buildPostFilterUrlFromState(
  { postFilters, postSearches, queryKey },
  baseUrl,
  options = {}
) {
  return buildPostFilterUrl(postFilters, postSearches, baseUrl, {
    queryKey,
    ...options,
  });
}

function syncPostFilterPaginationLinks(
  postFilters,
  postSearches,
  context = document,
  sourceUrl = new URL(window.location.href),
  queryKey = null
) {
  context
    .querySelectorAll('a.page-numbers, .gb-query-loop-pagination a')
    .forEach((paginationLink) => {
      try {
        const linkUrl = buildPostFilterUrl(postFilters, postSearches, paginationLink.href, {
          queryKey,
        });

        [...sourceUrl.searchParams.keys()].forEach((param) => {
          if (param.startsWith('query-') && param.endsWith('-page')) {
            const pageValue = new URL(
              paginationLink.href,
              window.location.origin
            ).searchParams.get(param);

            if (pageValue) {
              linkUrl.searchParams.set(param, pageValue);
            }
          }
        });

        paginationLink.href = linkUrl.toString();
      } catch (error) {
        // Ignore malformed URLs.
      }
  });
}

function syncPostFilterPaginationLinksFromState(
  { postFilters, postSearches, postFiltersGroup, queryKey },
  sourceUrl = new URL(window.location.href)
) {
  syncPostFilterPaginationLinks(
    postFilters,
    postSearches,
    postFiltersGroup,
    sourceUrl,
    queryKey
  );
}

function setPostFilterLoadingState(isLoading, queryKey = null) {
  getReplaceableTargets(document, queryKey).forEach((target) => {
    target.classList.toggle('is-loading', isLoading);
  });
}

function replacePostFilterQueryContent(nextDocument, queryKey = null) {
  const currentTargets = getReplaceableTargets(document, queryKey);
  const nextTargets = getReplaceableTargets(nextDocument, queryKey);

  currentTargets.forEach((currentTarget, index) => {
    let replacement = null;

    if (currentTarget.hasAttribute('data-gb-router-region')) {
      const regionId = currentTarget.getAttribute('data-gb-router-region');

      replacement = nextTargets.find(
        (nextTarget) =>
          nextTarget.getAttribute('data-gb-router-region') === regionId
      );
    }

    if (!replacement) {
      const currentClassNames = [...currentTarget.classList];

      replacement = nextTargets.find((nextTarget) =>
        currentClassNames.every((className) =>
          nextTarget.classList.contains(className)
        )
      );
    }

    if (!replacement) {
      replacement = nextTargets[index] || null;
    }

    if (replacement) {
      currentTarget.replaceWith(replacement);
    }
  });
}

function executePostCalendarScripts(context) {
  context.querySelectorAll('script').forEach((script) => {
    const replacement = document.createElement('script');

    [...script.attributes].forEach((attribute) => {
      replacement.setAttribute(attribute.name, attribute.value);
    });

    replacement.textContent = script.textContent;
    script.replaceWith(replacement);
  });
}

function getPostCalendarTarget(calendarButton, queryKey = null) {
  const section = calendarButton.closest('.gb-section, section') || document;
  const routerRegion = queryKey
    ? section.querySelector(`[data-gb-router-region="${queryKey}"]`)
    : null;
  const targetContext = routerRegion || section;

  return (
    targetContext.querySelector('.gb-block-container__grid') ||
    (targetContext.classList?.contains('gb-block-container__grid')
      ? targetContext
      : null) ||
    routerRegion ||
    section.querySelector('[data-gb-router-region]') ||
    null
  );
}

function getPostCalendarTemplate(calendarButton) {
  const group = calendarButton.closest('.gb-component-post-filters');

  return (
    group?.querySelector('template[data-post-calendar-template]') ||
    calendarButton.parentElement?.querySelector(
      'template[data-post-calendar-template]'
    ) ||
    null
  );
}

function setPostCalendarActive(calendarButton, state, isActive) {
  const {
    accordionButtons,
    postFilters,
    postSearches,
    postSorts,
    postFiltersGroup,
    queryKey,
  } = state;
  const target = getPostCalendarTarget(calendarButton, queryKey);
  const template = getPostCalendarTemplate(calendarButton);

  if (!target || !template) return;

  if (isActive) {
    target.postCalendarProgrammeContent = target.innerHTML;
    target.innerHTML = template.innerHTML;
    executePostCalendarScripts(target);
  } else {
    target.innerHTML = target.postCalendarProgrammeContent || '';
    target.postCalendarProgrammeContent = '';

    if (typeof window.starterDisableEventBookingButtons === 'function') {
      window.starterDisableEventBookingButtons(target);
    }
  }

  accordionButtons.forEach((accordionButton) => {
    accordionButton.disabled = isActive;
    accordionButton.setAttribute('aria-disabled', isActive ? 'true' : 'false');

    const filterContainer = accordionButton
      .closest('.gb-component-post-filters')
      ?.querySelector('.gb-component-post-filters__filters');

    if (filterContainer && isActive) {
      filterContainer.classList.remove(
        'gb-component-post-filters__filters--open'
      );
      filterContainer.hidden = true;
      accordionButton.setAttribute('aria-expanded', 'false');
      accordionButton.textContent =
        accordionButton.dataset.closedLabel || accordionButton.textContent;
    }
  });

  [...postFilters, ...postSorts].forEach((postFilterControl) => {
    const button = postFilterControl.querySelector(
      '.gb-component-post-filters__dropdown'
    );
    const menu = postFilterControl.querySelector('.gb-component-post-filters__menu');

    postFilterControl.classList.toggle(
      'gb-component-post-filters__control--disabled',
      isActive
    );

    if (button) {
      button.disabled = isActive;
      button.setAttribute('aria-disabled', isActive ? 'true' : 'false');
      button.setAttribute('aria-expanded', 'false');
    }

    if (menu && isActive) {
      menu.hidden = true;
    }

    if (isActive) {
      postFilterControl.classList.remove(
        'gb-component-post-filters__filter--open'
      );
    }
  });

  postSearches.forEach((postSearch) => {
    const searchInput = postSearch.querySelector(
      '.gb-component-post-filters__search-input'
    );

    postSearch.classList.toggle(
      'gb-component-post-filters__search--disabled',
      isActive
    );

    if (searchInput) {
      searchInput.disabled = isActive;
      searchInput.setAttribute('aria-disabled', isActive ? 'true' : 'false');
    }
  });

  postFiltersGroup.classList.toggle(
    'gb-component-post-filters--calendar-active',
    isActive
  );

  calendarButton.classList.toggle(
    'gb-component-post-filters__calendar--active',
    isActive
  );
  calendarButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  calendarButton.textContent = isActive
    ? calendarButton.dataset.programmeLabel || 'View programme'
    : calendarButton.dataset.calendarLabel || 'View calendar';
}

async function fetchAndReplacePostFilterResults(
  postFilters,
  postSearches,
  nextUrl,
  { pushState = true, queryKey = null } = {}
) {
  if (!getReplaceableTargets(document, queryKey).length) {
    window.location.href = nextUrl.toString();
    return;
  }

  setPostFilterLoadingState(true, queryKey);

  try {
    const response = await fetch(nextUrl.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const html = await response.text();
    const parser = new DOMParser();
    const nextDocument = parser.parseFromString(html, 'text/html');

    replacePostFilterQueryContent(nextDocument, queryKey);
    if (typeof window.starterDisableEventBookingButtons === 'function') {
      window.starterDisableEventBookingButtons(document);
    }
    syncPostFilterPaginationLinks(postFilters, postSearches, document, nextUrl, queryKey);

    if (pushState) {
      window.history.pushState({}, '', nextUrl.toString());
    }
  } catch (error) {
    window.location.href = nextUrl.toString();
  } finally {
    setPostFilterLoadingState(false, queryKey);
  }
}

function fetchAndReplacePostFilterResultsFromState(
  { postFilters, postSearches, queryKey },
  nextUrl,
  options = {}
) {
  return fetchAndReplacePostFilterResults(postFilters, postSearches, nextUrl, {
    queryKey,
    ...options,
  });
}

function initGbComponentPostFilters(context = document) {
  [...context.querySelectorAll('.gb-component-post-filters')].forEach(
    (postFiltersGroup) => {
      const state = getPostFiltersState(postFiltersGroup);
      const {
        postFilters,
        postSearches,
        postSorts,
        postCalendars,
        interactiveDropdowns,
        accordionButtons,
        queryKey,
      } = state;

      if (
        !postFilters.length &&
        !accordionButtons.length &&
        !postSearches.length &&
        !postSorts.length &&
        !postCalendars.length
      ) {
        return;
      }

      postFiltersGroup.dataset.queryKey = queryKey || '';

      accordionButtons.forEach((accordionButton) => {
        if (accordionButton.dataset.postFiltersReady === 'true') return;

        accordionButton.dataset.postFiltersReady = 'true';
        const baseLabel = (
          accordionButton.dataset.baseLabel || accordionButton.textContent.trim()
        ).replace(/\s*[+-]\s*$/, '');
        const closedLabel = `${baseLabel} +`;
        const openLabel = `${baseLabel} -`;

        accordionButton.dataset.baseLabel = baseLabel;
        accordionButton.dataset.openLabel = openLabel;
        accordionButton.dataset.closedLabel = closedLabel;
        accordionButton.textContent = closedLabel;

        accordionButton.addEventListener('click', () => {
          const filterContainer = accordionButton
            .closest('.gb-component-post-filters')
            ?.querySelector('.gb-component-post-filters__filters');

          if (!filterContainer) return;

          const isOpen = filterContainer.classList.toggle(
            'gb-component-post-filters__filters--open'
          );

          filterContainer.hidden = !isOpen;
          accordionButton.setAttribute(
            'aria-expanded',
            isOpen ? 'true' : 'false'
          );
          accordionButton.textContent = isOpen ? openLabel : closedLabel;
        });
      });

      postCalendars.forEach((calendarButton) => {
        if (calendarButton.dataset.postFiltersReady === 'true') return;

        calendarButton.dataset.postFiltersReady = 'true';
        calendarButton.dataset.calendarLabel =
          calendarButton.dataset.calendarLabel ||
          calendarButton.textContent.trim() ||
          'View calendar';
        calendarButton.dataset.programmeLabel =
          calendarButton.dataset.programmeLabel || 'View programme';
        calendarButton.textContent = calendarButton.dataset.calendarLabel;

        calendarButton.addEventListener('click', () => {
          const isActive =
            calendarButton.getAttribute('aria-pressed') === 'true';

          setPostCalendarActive(calendarButton, state, !isActive);
        });
      });

      postFilters.forEach((postFilter) => {
        if (postFilter.dataset.postFiltersReady === 'true') {
          updatePostFilterButtonLabel(postFilter);
          return;
        }

        postFilter.dataset.postFiltersReady = 'true';

        const button = postFilter.querySelector(
          '.gb-component-post-filters__dropdown'
        );
        const menu = postFilter.querySelector('.gb-component-post-filters__menu');
        const checkboxes = postFilter.querySelectorAll(
          '.gb-component-post-filters__checkbox-input'
        );

        updatePostFilterButtonLabel(postFilter);

        if (!button || !menu) return;

        button.addEventListener('click', () => {
          const isOpen = button.getAttribute('aria-expanded') === 'true';

          closePostFilterMenus(interactiveDropdowns, postFilter);
          button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          postFilter.classList.toggle(
            'gb-component-post-filters__filter--open',
            !isOpen
          );
          menu.hidden = isOpen;
        });

        postFilter.addEventListener('mouseleave', () => {
          closePostFilterMenus(interactiveDropdowns);
        });

        checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', () => {
            updatePostFilterButtonLabel(postFilter);

            const nextUrl = buildPostFilterUrlFromState(
              state,
              window.location.href,
              {
                resetPagination: true,
              }
            );

            fetchAndReplacePostFilterResultsFromState(state, nextUrl);
          });
        });
      });

      postSearches.forEach((postSearch) => {
        if (postSearch.dataset.postFiltersReady === 'true') return;

        postSearch.dataset.postFiltersReady = 'true';

        const postSearchInput = postSearch.querySelector(
          '.gb-component-post-filters__search-input'
        );

        if (!postSearchInput) return;

        postSearch.addEventListener('submit', (event) => {
          event.preventDefault();

          const nextUrl = buildPostFilterUrlFromState(
            state,
            window.location.href,
            {
              resetPagination: true,
            }
          );

          fetchAndReplacePostFilterResultsFromState(state, nextUrl);
        });
      });

      postSorts.forEach((postSort) => {
        if (postSort.dataset.postFiltersReady === 'true') return;

        postSort.dataset.postFiltersReady = 'true';

        const button = postSort.querySelector(
          '.gb-component-post-filters__dropdown'
        );
        const menu = postSort.querySelector('.gb-component-post-filters__menu');

        if (!button || !menu) return;

        button.addEventListener('click', () => {
          const isOpen = button.getAttribute('aria-expanded') === 'true';

          closePostFilterMenus(interactiveDropdowns, postSort);
          button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          postSort.classList.toggle(
            'gb-component-post-filters__filter--open',
            !isOpen
          );
          menu.hidden = isOpen;
        });

        postSort.addEventListener('mouseleave', () => {
          closePostFilterMenus(interactiveDropdowns);
        });
      });

      const currentUrl = new URL(window.location.href);

      syncPostFilterInputsFromUrl(postFilters, currentUrl, queryKey);
      syncPostSearchesFromUrl(postSearches, currentUrl, queryKey);
      syncPostFilterPaginationLinksFromState(state, currentUrl);
    }
  );
}

document.addEventListener('DOMContentLoaded', () => {
  initGbComponentPostFilters();

  document.querySelectorAll('[data-gb-router-region]').forEach((routerRegion) => {
    new MutationObserver(() => {
      initGbComponentPostFilters(routerRegion);
    }).observe(routerRegion, {
      childList: true,
      subtree: true,
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    closePostFilterMenus(getInteractivePostDropdowns());
  });

  document.addEventListener('mouseout', (event) => {
    if (event.relatedTarget || event.toElement) return;

    closePostFilterMenus(getInteractivePostDropdowns());
  });

  document.addEventListener('click', (event) => {
    const paginationLink = event.target.closest(
      'a.page-numbers, .gb-query-loop-pagination a'
    );

    if (!paginationLink) return;

    const postFiltersGroup = getPostFiltersGroupForElement(paginationLink);

    if (!postFiltersGroup) return;

    const state = getPostFiltersState(postFiltersGroup);

    if (!state.postFilters.length) return;

    event.preventDefault();

    const nextUrl = buildPostFilterUrlFromState(state, paginationLink.href);
    fetchAndReplacePostFilterResultsFromState(state, nextUrl);
  });

  window.addEventListener('popstate', () => {
    const nextUrl = new URL(window.location.href);

    [...document.querySelectorAll('.gb-component-post-filters')].forEach(
      (postFiltersGroup) => {
        const state = getPostFiltersState(postFiltersGroup);

        syncPostFilterInputsFromUrl(
          state.postFilters,
          nextUrl,
          state.queryKey
        );
        syncPostSearchesFromUrl(
          state.postSearches,
          nextUrl,
          state.queryKey
        );
        fetchAndReplacePostFilterResultsFromState(state, nextUrl, {
          pushState: false,
        });
      }
    );
  });
});
